import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../styles/components/matches-page.scss';
import MatchPageOverview from '../components/MatchPageOverview';
import GroupStandings from '../components/GroupStandings';
import BracketView from '../components/BracketView';
import PageHeader from '../components/PageHeader';
import LiveMatchBar from '../components/LiveMatchBar';
import MatchViewToggle, { MatchView } from '../components/MatchViewToggle';
import { CURRENT_SEASON, AVAILABLE_SEASONS } from '../Constants';
import { AuthContext } from '../context/AuthContext';

// Define Match and PlayedMatch types for LiveMatchBar
interface Team {
  teamId: string;
  teamName: string;
  teamLogo: string;
  season: number;
  division: string;
}

interface Match {
  challonge_id: string;
  team1: Team;
  team2: Team;
  isPlayoff: boolean;
  division: string;
  match_time: string;
  stream_url: string | null;
  season: number;
  hidden: boolean;
}

interface PlayedMatch {
  matchid: string;
  [key: string]: any;
}

const MatchesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useContext(AuthContext);

  // Get tab and season from URL params
  const urlTab = searchParams.get('tab') as 'matches' | 'groups' | 'bracket' | null;
  const urlSeason = searchParams.get('season');

  // State for active tab and season selection
  const [activeTab, setActiveTab] = useState<'matches' | 'groups' | 'bracket'>(
    urlTab && ['matches', 'groups', 'bracket'].includes(urlTab) ? urlTab : 'matches'
  );
  const [season, setSeason] = useState<number>(urlSeason ? parseInt(urlSeason) : CURRENT_SEASON);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [matchView, setMatchView] = useState<MatchView>('upcoming'); // New: toggle between upcoming and results
  const [sortBy, setSortBy] = useState<'date' | 'playoff' | 'alphabetical'>('date');

  // State for matches data (for LiveMatchBar)
  const [allMatches, setAllMatches] = useState<Match[]>([]);
  const [playedMatches, setPlayedMatches] = useState<PlayedMatch[]>([]);
  
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Set page title based on active tab
    const titles = {
      matches: 'Matches',
      groups: 'Group Standings',
      bracket: 'Tournament Bracket'
    };
    document.title = `${titles[activeTab]} - Northern Championships Series`;
  }, [activeTab]);
  
  // Handle tab selection
  const handleTabChange = (tab: 'matches' | 'groups' | 'bracket') => {
    setActiveTab(tab);
    
    // Update URL with new tab
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    
    // Keep season parameter for groups and bracket tabs
    if ((tab === 'groups' || tab === 'bracket') && season) {
      newSearchParams.set('season', season.toString());
    } else if (tab === 'matches') {
      // Remove season parameter for matches tab since it's not used
      newSearchParams.delete('season');
    }
    
    setSearchParams(newSearchParams);
  };
  
  // Handle season change
  const handleSeasonChange = (newSeason: number) => {
    setSeason(newSeason);
    // Reset match data immediately to avoid stale count calculations
    setAllMatches([]);
    setPlayedMatches([]);

    // Update URL with new season
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('season', newSeason.toString());
    setSearchParams(newSearchParams);
  };

  // Handle matches loaded from MatchPageOverview
  const handleMatchesLoaded = (matches: Match[], played: PlayedMatch[]) => {
    setAllMatches(matches);
    setPlayedMatches(played);
  };

  // Calculate match counts for toggle
  const upcomingCount = allMatches
    .filter(m => isAdmin || !m.hidden) // Filter hidden matches for non-admins
    .filter(m => {
      const isPlayed = playedMatches.some(pm => pm.matchid === m.challonge_id);
      if (isPlayed) return false;

      // Check if it's live (started within 6 hours)
      const matchTime = new Date(m.match_time);
      const now = new Date();
      const hoursSinceStart = (now.getTime() - matchTime.getTime()) / (1000 * 60 * 60);
      const isLive = hoursSinceStart >= 0 && hoursSinceStart <= 6;

      return !isLive; // Count only non-live upcoming matches
    }).length;

  const resultsCount = playedMatches.filter(pm => {
    // For results, also check if the match is hidden (if we have that info)
    const matchData = allMatches.find(m => m.challonge_id === pm.matchid);
    return isAdmin || !matchData?.hidden;
  }).length;

  // Sync state with URL params
  useEffect(() => {
    const urlTab = searchParams.get('tab') as 'matches' | 'groups' | 'bracket' | null;
    const urlSeason = searchParams.get('season');
    
    if (urlTab && ['matches', 'groups', 'bracket'].includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    
    if (urlSeason) {
      const seasonNum = parseInt(urlSeason);
      if (seasonNum !== season && AVAILABLE_SEASONS.includes(seasonNum)) {
        setSeason(seasonNum);
      }
    }
  }, [searchParams, activeTab, season]);

  // Initialize URL with default tab if not present
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    
    if (!urlTab) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', activeTab);
      
      // Add season parameter if on groups or bracket tab
      if ((activeTab === 'groups' || activeTab === 'bracket') && season) {
        newSearchParams.set('season', season.toString());
      }
      
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, activeTab, season, setSearchParams]);

  // Auto-switch to results view for previous seasons (no upcoming matches in the past)
  useEffect(() => {
    if (season !== CURRENT_SEASON && matchView === 'upcoming') {
      setMatchView('results');
    }
  }, [season, matchView]);


  return (
    <div className="matches-page">
      <PageHeader 
        title={
          activeTab === 'matches' ? 'MATCHES' :
          activeTab === 'groups' ? 'GROUP STANDINGS' :
          'TOURNAMENT BRACKET'
        }
        subtitle={
          activeTab === 'matches' ? 'View all matches in the Northern Championship Series' :
          activeTab === 'groups' ? 'Track team performance and standings in the tournament groups' :
          'Follow the tournament progression and playoff matches'
        }
      >
        <div className="tab-buttons">
          <button 
            className={`tab-button ${activeTab === 'matches' ? 'active' : ''}`}
            onClick={() => handleTabChange('matches')}
          >
            Matches
          </button>
          <button 
            className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => handleTabChange('groups')}
          >
            Groups
          </button>
          <button 
            className={`tab-button ${activeTab === 'bracket' ? 'active' : ''}`}
            onClick={() => handleTabChange('bracket')}
          >
            Bracket
          </button>
        </div>
        
        {/* Search for matches tab */}
        {activeTab === 'matches' && (
          <input
            type="text"
            placeholder="Search teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        )}
        
        {/* Season selector is only shown for Groups and Bracket views */}
        {(activeTab === 'groups' || activeTab === 'bracket') && (
          <div className="season-selector">
            <select
              value={season}
              onChange={(e) => handleSeasonChange(parseInt(e.target.value))}
            >
              {AVAILABLE_SEASONS.map(s => (
                <option key={s} value={s}>Season {s}</option>
              ))}
            </select>
          </div>
        )}
      </PageHeader>
      
      <div className="container">
        <div className="matches-content">
          {/* Render the appropriate component based on active tab */}
          {activeTab === 'matches' && (
            <>
              <LiveMatchBar
                selectedSeason={season}
                currentSeason={CURRENT_SEASON}
                maxDisplay={4}
                isAdmin={isAdmin}
              />

              <div className="matches-filter-controls">
                {season === CURRENT_SEASON && (
                  <MatchViewToggle
                    view={matchView}
                    onViewChange={setMatchView}
                    upcomingCount={upcomingCount}
                    resultsCount={resultsCount}
                  />
                )}

                <select
                  value={season}
                  onChange={(e) => handleSeasonChange(parseInt(e.target.value))}
                  className="filter-button"
                >
                  {AVAILABLE_SEASONS.map(s => (
                    <option key={s} value={s}>Season {s}</option>
                  ))}
                </select>
              </div>

              <MatchPageOverview
                searchTerm={searchTerm}
                matchStatusFilter="all"
                sortBy={sortBy}
                season={season}
                view={season === CURRENT_SEASON ? matchView : 'results'}
                onMatchesLoaded={handleMatchesLoaded}
                isAdmin={isAdmin}
              />
            </>
          )}
          
          {activeTab === 'groups' && (
            <GroupStandings initialTab="groups" season={season} />
          )}
          
          {activeTab === 'bracket' && (
            <BracketView season={season} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchesPage;